/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { getRouteInitData, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource } from '@gh/core-data';
import { DialogService } from '@gh/core-ui';

import {
  CustomFieldDetailsDialogConfig,
  CustomFieldListBusinessService,
} from '../../resources/custom-field-list-business.service';
import { CustomFieldDetails } from '../../resources/custom-field.model';
import { customFieldDetailsDialog } from '../custom-field-details-dialog/custom-field-details-dialog.route';
import { CustomFieldListTabGridDefinition } from './custom-field-list-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-custom-field-list-tab',
  templateUrl: 'custom-field-list-tab.component.html',
  styleUrls: ['custom-field-list-tab.component.scss'],
  viewProviders: [CustomFieldListTabGridDefinition],
})
export class CustomFieldListTabComponent implements OnInit {
  customField: CustomFieldDetails;
  customFields: SlicedDataSource<CustomFieldDetails>;
  dataSource: SlicedDataSource<CustomFieldDetails>;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private businessService: CustomFieldListBusinessService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              public gridDefinition: CustomFieldListTabGridDefinition) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);
    this.loadData();
  }

  reload(): void {
    this.loadData();
  }

  loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource();
  }

  onCreate(): void {
    this.dialogService.openWithInput(customFieldDetailsDialog, {})
      .afterClosed().subscribe((customField) => {
      if (customField) {
        this.reload();
      }
    });
  }

  onEdit(customField: CustomFieldDetails): void {
    if (this.userContext.hasPermission(BuilderPermission.Setup.Contacts.CustomFields.EDIT)) {
      this.dialogService
        .openWithInput(customFieldDetailsDialog, <CustomFieldDetailsDialogConfig>{
          id: customField.customFieldId,
        })
        .afterClosed()
        .subscribe((newField) => {
          if (newField) {
            this.reload();
          }
        });
    }
  }

  onDeactivate(customField: CustomFieldDetails): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_custom_field_deactivate',
      execute: this.commonActions.execute((state: CommonFlowState<CustomFieldDetails>) =>
        this.businessService.deactivate(state.value)),
      finish: () => this.reload(),
    });

    flow.run({
      value: customField,
    });
  }

  onActivate(customField: CustomFieldDetails): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_custom_field_activate',
      execute: this.commonActions.execute((state: CommonFlowState<CustomFieldDetails>) =>
        this.businessService.activate(state.value)),
      finish: () => this.reload(),
    });

    flow.run({
      value: customField,
    });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'CUSTOM_FIELD',
      label: label ? label : 'lbl_custom_fields',
    });
  }

}
